import type { ImportHistoryEntry } from "@/api/researchCms.service";
import { ROUTES } from "@/routes";
import { MOVE_NEEDS_DELETE_REASON } from "../components/BpaImport/importText";
import { ARCHIVE_NEEDS_DELETE_REASON } from "./bpaArchive";
import { ALL_KINDS, type ImportKind, plural } from "./bpaImport";
import { importBlockedReason, isEditionLocked, moveBlockedReason } from "./bpaImportEditionStatus";
import { writeImportUrl } from "./bpaImportUrlState";

export const MERGE_UNSAVED_REASON = "Save or discard your changes first";
export function mergeMoveLine(sourceName: string): string {
  return `A merge moves every content type out of ${sourceName}. Firms you untick stay there.`;
}
function archiveButtonText(count: number): string {
  return count === 1 ? "Archive 1 practice area" : `Archive ${count} practice areas`;
}

export const MERGE_BADGE_LABEL = "Scheduled for merge";
export const MERGE_IN_PROGRESS_LABEL = "Merge in progress";
export const SPLIT_OPEN_QUERY_KEY = "research-merge-split-open";

export type MergeRow = {
  id: string;
  bpaId: string;
  bpaName: string;
  status: string;
  mergeTargetEditionBpaId: string | null;
  mergeTargetBpaId?: string | null;
  mergeSourceCount: number;
};

export function toMergeRow(row: {
  id: string;
  bpaId: string;
  bpaName: string;
  status?: string | null;
  mergeTargetEditionBpaId?: string | null;
  mergeTargetBpaId?: string | null;
  mergeSourceCount?: number;
}): MergeRow {
  return {
    id: row.id,
    bpaId: row.bpaId,
    bpaName: row.bpaName,
    status: row.status ?? "",
    mergeTargetEditionBpaId: row.mergeTargetEditionBpaId ?? null,
    mergeTargetBpaId: row.mergeTargetBpaId ?? null,
    mergeSourceCount: row.mergeSourceCount ?? 0,
  };
}

export type MergeHoldings = { organisations: number; items: number; noFirm: boolean };

export type MergeTargetChoice = { kind: "existing"; row: MergeRow } | { kind: "create" };

export type MergeCard = {
  editionBpaId: string;
  bpaId: string;
  name: string;
  archived: boolean;
  imported: boolean;
  contentBroughtIn: boolean;
  done: boolean;
  statusLine: string;
  importedAt: string | null;
  importVisible: boolean;
  importLabel: string;
  removeVisible: boolean;
};

export type MergeHub = {
  cards: MergeCard[];
  doneCount: number;
  progress: string;
  archiveOffered: boolean;
  archiveLabel: string | null;
  archiveButtonLabel: string | null;
  archiveEditionBpaIds: string[];
  emptyText: string | null;
};

const MIN_MERGE_SOURCES = 2;
const MAX_MERGE_SOURCES = 5;
const MERGE_NEEDS_DELETE_REASON =
  "Merging moves content out of the practice areas and needs delete permission.";
const REMOVE_NEEDS_DELETE_REASON = "Removing from a merge needs delete permission.";
const ARCHIVE_WARNING = (count: number) => {
  const them = count === 1 ? "it" : "them";
  return `Are you sure you want to archive ${them}? This will hide ${them} from the website and all related applications.`;
};

export function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function isArchived(row: Pick<MergeRow, "status">): boolean {
  return row.status === "archived";
}

function hasMergeImport(
  entry: ImportHistoryEntry,
  sourceEditionBpaId: string,
  targetEditionBpaId: string,
): boolean {
  return (
    entry.purpose === "merge" &&
    entry.sourceEditionBpaId === sourceEditionBpaId &&
    entry.targetEditionBpaId === targetEditionBpaId
  );
}

function hubPath(editionId: string, targetBpaId: string): string {
  return ROUTES.research.cmsBpaMerge(editionId, targetBpaId);
}

export function isInMerge(
  row: Pick<MergeRow, "mergeTargetEditionBpaId" | "mergeSourceCount">,
): boolean {
  return row.mergeTargetEditionBpaId !== null || row.mergeSourceCount > 0;
}

export function inMergeBlockedReason(ticked: MergeRow[]): string | null {
  const inMerge = ticked.filter(isInMerge);
  if (inMerge.length === 0) return null;
  return `${joinNames(inMerge.map(row => row.bpaName))} already in a merge. Finish it, or remove its practice areas from the merge, first.`;
}

function tickedBlockedReason(input: { ticked: MergeRow[]; splitOpenIds: string[] }): string | null {
  const inMergeReason = inMergeBlockedReason(input.ticked);
  if (inMergeReason) return inMergeReason;
  const inSplit = input.ticked.filter(row => input.splitOpenIds.includes(row.id));
  if (inSplit.length > 0) {
    return `${joinNames(inSplit.map(row => row.bpaName))} has content moved out by a split. Finish the split first.`;
  }
  return null;
}

export function mergeActionState(input: {
  flagOn: boolean;
  canEdit: boolean;
  canDelete: boolean;
  editionStatus: string | null;
  isDirty: boolean;
  ticked: MergeRow[];
  splitOpenIds: string[];
}): { visible: boolean; disabledReason: string | null } {
  if (!input.flagOn || !input.canEdit) return { visible: false, disabledReason: null };
  const disabledReason = (() => {
    if (input.isDirty) return MERGE_UNSAVED_REASON;
    if (!input.canDelete) return MERGE_NEEDS_DELETE_REASON;
    const lockedReason = moveBlockedReason(input.editionStatus);
    if (lockedReason) return lockedReason;
    if (input.ticked.length < MIN_MERGE_SOURCES || input.ticked.length > MAX_MERGE_SOURCES) {
      return `Tick ${MIN_MERGE_SOURCES} to ${MAX_MERGE_SOURCES} practice areas to merge.`;
    }
    return tickedBlockedReason(input);
  })();
  return { visible: true, disabledReason };
}

export function mergeTargetChoices(input: {
  ticked: MergeRow[];
  editionRows: MergeRow[];
  splitOpenIds: string[];
}): MergeTargetChoice[] {
  const tickedIds = input.ticked.map(row => row.id);
  const others = input.editionRows
    .filter(
      row =>
        !tickedIds.includes(row.id) &&
        !isArchived(row) &&
        row.mergeTargetEditionBpaId === null &&
        !input.splitOpenIds.includes(row.id),
    )
    .map((row): MergeTargetChoice => ({ kind: "existing", row }));
  return [...others, { kind: "create" }];
}

export const CHOOSE_TARGET_REASON = "Choose where the content goes.";

export function startMergeState(input: { ticked: MergeRow[]; target: MergeTargetChoice | null }): {
  disabledReason: string | null;
  sourceEditionBpaIds: string[];
} {
  if (!input.target) return { disabledReason: CHOOSE_TARGET_REASON, sourceEditionBpaIds: [] };
  const target = input.target;
  const sourceEditionBpaIds = input.ticked.map(row => row.id);
  if (
    target.kind === "existing" &&
    target.row.mergeSourceCount + sourceEditionBpaIds.length > MAX_MERGE_SOURCES
  ) {
    return {
      disabledReason: `${target.row.bpaName} can have at most ${MAX_MERGE_SOURCES} practice areas scheduled into it.`,
      sourceEditionBpaIds,
    };
  }
  return { disabledReason: null, sourceEditionBpaIds };
}

export function newTargetNameProblem(name: string, editionNames: string[]): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Give the new practice area a name.";
  const taken = editionNames.map(existing => existing.trim().toLowerCase());
  if (taken.includes(trimmed.toLowerCase())) {
    return `${trimmed} is already a practice area name in this edition.`;
  }
  return null;
}

export function mergeHubUrl(editionId: string, targetBpaId: string): string {
  return hubPath(editionId, targetBpaId);
}

function hubTargetBpaId(row: MergeRow, rows: MergeRow[]): string | null {
  if (row.mergeTargetEditionBpaId) {
    if (row.mergeTargetBpaId) return row.mergeTargetBpaId;
    return rows.find(candidate => candidate.id === row.mergeTargetEditionBpaId)?.bpaId ?? null;
  }
  return row.mergeSourceCount > 0 ? row.bpaId : null;
}

export function mergeBadge(
  row: MergeRow,
  rows: MergeRow[],
  options: { editionId: string; flagOn: boolean },
): { label: string; href: string } | null {
  if (!options.flagOn) return null;
  const targetBpaId = hubTargetBpaId(row, rows);
  if (!targetBpaId) return null;
  return { label: MERGE_BADGE_LABEL, href: mergeHubUrl(options.editionId, targetBpaId) };
}

export function mergeBanner(
  row: MergeRow,
  rows: MergeRow[],
  options: { editionId: string; flagOn: boolean },
): { title: string; body: string; actionLabel: string; href: string } | null {
  const badge = mergeBadge(row, rows, options);
  if (!badge) return null;
  if (row.mergeTargetEditionBpaId) {
    return {
      title: MERGE_BADGE_LABEL,
      body: "This practice area is waiting to be merged into another one.",
      actionLabel: "Continue merge",
      href: badge.href,
    };
  }
  return {
    title: MERGE_IN_PROGRESS_LABEL,
    body: "Other practice areas are being merged into this one.",
    actionLabel: "Continue merge",
    href: badge.href,
  };
}

function holdingsLeft(holdings: MergeHoldings): boolean {
  return holdings.organisations > 0 || holdings.items > 0 || holdings.noFirm;
}

function leftDescription(holdings: MergeHoldings): string {
  const organisations = plural(holdings.organisations, "organisation");
  return holdings.noFirm ? `${organisations} and No firm` : organisations;
}

function leftAfterImportText(holdings: MergeHoldings | undefined): string | null {
  if (holdings === undefined) return null;
  if (holdings.noFirm) {
    return holdings.organisations > 0 ? `${leftDescription(holdings)} left` : "No firm left";
  }
  return holdings.organisations > 0 ? `${holdings.organisations} left` : null;
}

function importedStatusLine(
  entries: ImportHistoryEntry[],
  holdings: MergeHoldings | undefined,
): string {
  const brought = new Set(
    entries.flatMap(entry => entry.copiedCompanies.map(company => company.id)),
  );
  const parts = [`${plural(brought.size, "organisation")} imported`];
  const leftText = leftAfterImportText(holdings);
  if (leftText) parts.push(leftText);
  return parts.join(", ");
}

function hasContentCopied(entry: ImportHistoryEntry): boolean {
  return (
    entry.copiedCompanies.length > 0 ||
    entry.importedSubmissions + entry.importedRankings + entry.importedCommentary > 0
  );
}

function mergeCard(
  row: MergeRow,
  entries: ImportHistoryEntry[],
  holdings: MergeHoldings | undefined,
  locked: boolean,
): MergeCard {
  const imported = entries.length > 0;
  const archived = isArchived(row);
  const holdingsKnown = holdings !== undefined;
  const left = holdingsKnown && holdingsLeft(holdings);
  const nothingToBringIn = !imported && holdingsKnown && !left;
  const leftInLockedEdition = !imported && left && locked;
  const statusLine = imported
    ? importedStatusLine(entries, holdings)
    : holdings === undefined
      ? MERGE_BADGE_LABEL
      : left
        ? `${MERGE_BADGE_LABEL} · ${leftDescription(holdings)}`
        : "Nothing to bring in";
  return {
    editionBpaId: row.id,
    bpaId: row.bpaId,
    name: row.bpaName,
    archived,
    imported,
    contentBroughtIn: entries.some(hasContentCopied),
    done: imported || nothingToBringIn || leftInLockedEdition,
    statusLine,
    importedAt: imported ? entries[0].createdAt : null,
    importVisible: !archived && (imported ? left : !holdingsKnown || left),
    importLabel: imported ? "Import the rest" : "Import",
    removeVisible: !archived,
  };
}

export function mergeHubModel(input: {
  target: MergeRow;
  rows: MergeRow[];
  history: ImportHistoryEntry[];
  holdings: Record<string, MergeHoldings>;
  editionStatus: string | null;
}): MergeHub {
  const locked = isEditionLocked(input.editionStatus);
  const newestFirst = [...input.history].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const entriesFor = (row: MergeRow) =>
    newestFirst.filter(entry => hasMergeImport(entry, row.id, input.target.id));
  const cards = input.rows
    .filter(
      row =>
        row.id !== input.target.id &&
        (row.mergeTargetEditionBpaId === input.target.id ||
          (isArchived(row) && entriesFor(row).length > 0)),
    )
    .map(row => mergeCard(row, entriesFor(row), input.holdings[row.id], locked));
  const doneCount = cards.filter(card => card.done).length;
  const remaining = cards.filter(card => !card.archived);
  const archiveOffered = cards.length > 0 && doneCount === cards.length && remaining.length > 0;
  return {
    cards,
    doneCount,
    progress: `${doneCount} of ${cards.length} done`,
    archiveOffered,
    archiveLabel: archiveOffered ? `Archive ${joinNames(remaining.map(card => card.name))}` : null,
    archiveButtonLabel: archiveOffered ? archiveButtonText(remaining.length) : null,
    archiveEditionBpaIds: archiveOffered ? remaining.map(card => card.editionBpaId) : [],
    emptyText:
      cards.length === 0
        ? `Nothing is scheduled into ${input.target.bpaName}. ${input.target.bpaName} stays as it is.`
        : null,
  };
}

export function mergeImportUrl(input: {
  editionId: string;
  targetBpaId: string;
  sourceEditionBpaId: string;
}): string {
  const params = writeImportUrl(
    {
      sourceEditionBpaId: input.sourceEditionBpaId,
      sourceEditionId: input.editionId,
      kinds: ALL_KINDS,
      mode: "move",
      purpose: "merge",
      hub: mergeHubUrl(input.editionId, input.targetBpaId),
    },
    input.editionId,
  );
  return `${ROUTES.research.cmsBpaImport(input.editionId, input.targetBpaId)}?${params.toString()}`;
}

export function isMergeHubForImport(
  hub: string,
  importing: { editionId: string; targetBpaId: string; sourceScheduledIntoTarget: boolean },
): boolean {
  if (!importing.sourceScheduledIntoTarget) return false;
  if (!hub.startsWith("/") || hub.startsWith("//")) return false;
  const path = hub.split(/[?#]/)[0];
  return path === hubPath(importing.editionId, importing.targetBpaId);
}

export function holdingsFromSummary(
  firms: Array<{
    companyId: string | null;
    submissions: number;
    rankings: number;
    commentary: number;
  }>,
): MergeHoldings {
  const withContent = firms.filter(firm => firm.submissions + firm.rankings + firm.commentary > 0);
  return {
    organisations: withContent.filter(firm => firm.companyId !== null).length,
    items: withContent.reduce(
      (sum, firm) => sum + firm.submissions + firm.rankings + firm.commentary,
      0,
    ),
    noFirm: withContent.some(firm => firm.companyId === null),
  };
}

export function mergeHubPermissions(input: {
  flagOn: boolean;
  canRead: boolean;
  canEdit: boolean;
  canDelete: boolean;
  editionStatus: string | null;
}): {
  open: boolean;
  viewOnly: boolean;
  importDisabledReason: string | null;
  removeDisabledReason: string | null;
  archiveDisabledReason: string | null;
} {
  return {
    open: input.flagOn && input.canRead,
    viewOnly: !input.canEdit,
    importDisabledReason: input.canDelete
      ? importBlockedReason(input.editionStatus)
      : MOVE_NEEDS_DELETE_REASON,
    removeDisabledReason: input.canDelete ? null : REMOVE_NEEDS_DELETE_REASON,
    archiveDisabledReason: input.canDelete ? null : ARCHIVE_NEEDS_DELETE_REASON,
  };
}

export function mergeArchiveConfirmation(input: {
  cards: MergeCard[];
  holdings: Record<string, MergeHoldings>;
}): string[] {
  const toArchive = input.cards.filter(card => !card.archived);
  const leftLines = toArchive.flatMap(card => {
    const holdings = input.holdings[card.editionBpaId];
    if (!holdings || !holdingsLeft(holdings)) return [];
    const items = plural(holdings.items, "item");
    return [
      `${card.name} still has ${leftDescription(holdings)} and ${items} that were not imported. Archiving hides them.`,
    ];
  });
  return [...leftLines, ARCHIVE_WARNING(toArchive.length)];
}

export function mergeRemoveConfirmation(input: {
  card: MergeCard;
  cards: MergeCard[];
  targetName: string;
}): { title: string; message: string; endsMerge: boolean } {
  const { card, cards, targetName } = input;
  const endsMerge = !cards.some(
    other => other.editionBpaId !== card.editionBpaId && !other.archived,
  );
  const whatStays = card.imported
    ? `The content already moved into ${targetName} stays there, and ${card.name} won't be archived with this merge.`
    : `It stays as it is and is no longer scheduled into ${targetName}.`;
  return {
    title: `Remove ${card.name} from this merge?`,
    message: endsMerge
      ? `${whatStays} It's the last practice area in this merge, so the merge ends.`
      : whatStays,
    endsMerge,
  };
}

export type MergedOutcome = { broughtIn: string[]; keptOut: string[] };

export function mergeFinishOutcome(input: {
  cards: MergeCard[];
  holdings: Record<string, MergeHoldings>;
}): { sourceNames: string[]; outcome: MergedOutcome } {
  const named = input.cards.filter(card => !card.archived || card.contentBroughtIn);
  const stayed = (card: MergeCard) => {
    const holdings = input.holdings[card.editionBpaId];
    return !card.archived && holdings !== undefined && holdingsLeft(holdings);
  };
  return {
    sourceNames: named.map(card => card.name),
    outcome: {
      broughtIn: named.filter(card => card.contentBroughtIn).map(card => card.name),
      keptOut: named.filter(stayed).map(card => card.name),
    },
  };
}

export function mergedMessage(sourceNames: string[], outcome: MergedOutcome): string {
  const single = sourceNames.length === 1;
  const merged = `${joinNames(sourceNames)} ${single ? "has" : "have"} been merged into this practice area and archived.`;
  if (outcome.keptOut.length === 0) return merged;
  return `${merged} Any content that was not imported remains with the archived practice ${single ? "area" : "areas"}.`;
}

export const MERGE_ADD_LIMIT_REASON = `A merge can have at most ${MAX_MERGE_SOURCES} practice areas.`;
const MERGE_ADD_NEEDS_DELETE_REASON = "Adding to a merge needs delete permission.";

function openCards(cards: MergeCard[]): MergeCard[] {
  return cards.filter(card => !card.archived);
}

export function mergeAddAction(input: {
  cards: MergeCard[];
  canDelete: boolean;
  editionStatus: string | null;
}): { offered: boolean; disabledReason: string | null } {
  const open = openCards(input.cards);
  if (open.length === 0) return { offered: false, disabledReason: null };
  const disabledReason = (() => {
    if (!input.canDelete) return MERGE_ADD_NEEDS_DELETE_REASON;
    const lockedReason = moveBlockedReason(input.editionStatus);
    if (lockedReason) return lockedReason;
    if (open.length >= MAX_MERGE_SOURCES) return MERGE_ADD_LIMIT_REASON;
    return null;
  })();
  return { offered: true, disabledReason };
}

export function mergeAddCandidates(input: {
  target: MergeRow;
  rows: MergeRow[];
  splitOpenIds: string[];
}): MergeRow[] {
  return input.rows
    .filter(
      row =>
        !isArchived(row) &&
        row.id !== input.target.id &&
        !isInMerge(row) &&
        !input.splitOpenIds.includes(row.id),
    )
    .sort((a, b) => a.bpaName.localeCompare(b.bpaName));
}

export function mergeAddSourceIds(cards: MergeCard[], addedEditionBpaId: string): string[] {
  return [...openCards(cards).map(card => card.editionBpaId), addedEditionBpaId];
}

export function mergeBlockedHubHref(input: {
  editionId: string;
  ticked: MergeRow[];
  rows: MergeRow[];
}): string | null {
  const inMerge = input.ticked.find(isInMerge);
  if (!inMerge) return null;
  const targetBpaId = hubTargetBpaId(inMerge, input.rows);
  return targetBpaId ? mergeHubUrl(input.editionId, targetBpaId) : null;
}
